import React, { createContext, useState, useCallback } from 'react';
import getMenuTree from '../ApiV2/getMenuTree';
import { cacheAPIResponse, getCachedResponse } from '../Helpers/SessionStorage';

export const TreeContext = createContext();

export const TreeProvider = ({ children }) => {
  const [treeData, setTreeData] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeTreePayload = (payload) => {
    if (!payload) return null;
    if (Array.isArray(payload)) {
      return { menus: payload };
    }
    if (payload.menus) {
      return payload;
    }
    return null;
  };

  const fetchTree = useCallback((location) => {
    // If already loaded for this location, don't fetch again
    if (treeData && currentLocation === location) {
      return Promise.resolve(treeData);
    }

    setIsLoading(true);
    const cacheKey = `menuTree-${location}`;
    const cachedMenu = getCachedResponse(cacheKey);

    if (cachedMenu) {
      const normalizedTree = normalizeTreePayload(cachedMenu);
      if (normalizedTree) {
        setTreeData(normalizedTree);
        setCurrentLocation(location);
        setIsLoading(false);
        return Promise.resolve(normalizedTree);
      }
    }

    return getMenuTree(location, true)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const normalizedTree = normalizeTreePayload(data);
        if (!normalizedTree || !normalizedTree.menus) {
          throw new Error("Invalid menu data received");
        }
        setTreeData(normalizedTree);
        setCurrentLocation(location);
        cacheAPIResponse(cacheKey, normalizedTree);
        setError(null);
        setIsLoading(false);
        return normalizedTree;
      })
      .catch((error) => {
        console.error("Error loading menu tree:", error);
        setError(error.message);
        setIsLoading(false);
        throw error;
      });
  }, [treeData, currentLocation]);

  const findMenuById = useCallback((menuId) => {
    if (!treeData || !treeData.menus) return null;
    const numericMenuId = Number(menuId);
    return treeData.menus.find(menu => menu.menuId === numericMenuId);
  }, [treeData]);

  const findSubmenuById = useCallback((submenuId) => {
    if (!treeData || !treeData.menus) return null;
    const numericSubmenuId = Number(submenuId);
    
    // Recursive function to search through nested submenus
    const searchSubmenus = (submenus) => {
      for (let submenu of submenus) {
        if (submenu.submenuId === numericSubmenuId) {
          return submenu;
        }
        // Recursively search in children if they exist
        if (submenu.children && submenu.children.length > 0) {
          const found = searchSubmenus(submenu.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    for (let menu of treeData.menus) {
      if (menu.submenus) {
        const found = searchSubmenus(menu.submenus);
        if (found) return found;
      }
    }
    return null;
  }, [treeData]);

  const findItemById = useCallback((itemId) => {
    if (!treeData || !treeData.menus) return null;
    const numericItemId = Number(itemId);
    
    for (let menu of treeData.menus) {
      if (menu.submenus) {
        for (let submenu of menu.submenus) {
          if (submenu.items) {
            const item = submenu.items.find(item => item.menuItemId === numericItemId);
            if (item) return item;
          }
          // Also search in children's items
          if (submenu.children) {
            for (let child of submenu.children) {
              if (child.items) {
                const item = child.items.find(item => item.menuItemId === numericItemId);
                if (item) return item;
              }
            }
          }
        }
      }
    }
    return null;
  }, [treeData]);

  const value = {
    treeData,
    currentLocation,
    isLoading,
    error,
    fetchTree,
    findMenuById,
    findSubmenuById,
    findItemById,
  };

  return (
    <TreeContext.Provider value={value}>
      {children}
    </TreeContext.Provider>
  );
};
